import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { CheckCircle, Clock, WarningCircle, Eye, Users, CalendarCheck, TrendUp, Phone, X, Archive } from '@phosphor-icons/react';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';
import { fmtDate as fmtDatePt } from '@/utils/dateFmt';


const API = process.env.REACT_APP_BACKEND_URL;
const STATUS_CFG = { enviado: { label: 'Feito', color: 'bg-brand-green/10 text-brand-green', icon: CheckCircle }, em_falta: { label: 'Pedido enviado', color: 'bg-brand-gold/10 text-brand-gold-hover', icon: Clock }, em_atraso: { label: 'Em atraso', color: 'bg-red-50 text-red-500', icon: WarningCircle }, arquivada: { label: 'Arquivada', color: 'bg-brand-bg text-brand-muted', icon: Archive } };

export default function AdminDashboardPage() {
  useRequireAdmin();
  const [daily, setDaily] = useState(null);
  const [mgmt, setMgmt] = useState(null);
  const [view, setView] = useState('daily'); // daily, management, recontact, list
  const [listData, setListData] = useState({ title: '', ids: [] });
  const [allPatients, setAllPatients] = useState([]);
  const [period, setPeriod] = useState('30');
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const navigate = useNavigate();

  const fetchAll = useCallback(async (includeArchived) => {
    try {
      const [d, m, p] = await Promise.all([
        axios.get(`${API}/api/admin/daily-dashboard`, { params: { show_archived: includeArchived } }),
        axios.get(`${API}/api/admin/management-dashboard`),
        axios.get(`${API}/api/patients`),
      ]);
      setDaily(d.data); setMgmt(m.data); setAllPatients(p.data);
    } catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchAll(showArchived); }, [fetchAll, showArchived]);

  const markContacted = async (pid) => {
    await axios.put(`${API}/api/admin/mark-contacted/${pid}`);
    fetchAll(showArchived);
  };

  const openList = (title, ids) => { setListData({ title, ids }); setView('list'); };
  const getPatient = (id) => allPatients.find(p => p.user_id === id) || { name: '?', email: '' };

  if (loading) return <div className="text-center py-12 text-brand-muted">A carregar...</div>;

  return (
    <div className="fade-in" data-testid="admin-dashboard">
      {/* View toggle */}
      <div className="flex gap-2 mb-5">
        <button onClick={() => setView('daily')} className={`px-4 py-2 rounded-full text-xs font-medium ${view === 'daily' ? 'bg-brand-green text-white' : 'bg-white border border-brand-border text-brand-muted'}`}>Hoje</button>
        <button onClick={() => setView('management')} className={`px-4 py-2 rounded-full text-xs font-medium ${view === 'management' ? 'bg-brand-green text-white' : 'bg-white border border-brand-border text-brand-muted'}`}>Gestão</button>
      </div>

      {/* ═══ DAILY VIEW ═══ */}
      {view === 'daily' && daily && (
        <>

          <div className="mb-5">
            <h2 className="font-heading font-medium text-2xl text-brand-text capitalize">Hoje é {daily.day_name}</h2>
            <p className="text-sm text-brand-green font-medium mt-0.5">{daily.label}</p>
          </div>
          <div className="grid grid-cols-4 gap-3 mb-5">
            <div className="bg-white border border-brand-border rounded-2xl p-3 text-center"><p className="text-2xl font-heading font-semibold text-brand-text">{daily.summary?.total}</p><p className="text-[10px] text-brand-muted">Pacientes</p></div>
            <div className="bg-brand-green/5 border border-brand-green/15 rounded-2xl p-3 text-center"><p className="text-2xl font-heading font-semibold text-brand-green">{daily.summary?.sent}</p><p className="text-[10px] text-brand-muted">Feitos</p></div>
            <div className="bg-brand-gold/5 border border-brand-gold/15 rounded-2xl p-3 text-center"><p className="text-2xl font-heading font-semibold text-brand-gold-hover">{daily.summary?.missing}</p><p className="text-[10px] text-brand-muted">Pedidos enviados</p></div>
            <div className="bg-red-50 border border-red-100 rounded-2xl p-3 text-center"><p className="text-2xl font-heading font-semibold text-red-500">{daily.summary?.late}</p><p className="text-[10px] text-brand-muted">Em atraso</p></div>
          </div>
          {daily.patients?.length > 0 && (
            <div className="bg-white border border-brand-border rounded-2xl overflow-hidden mb-5">
              <div className="flex items-center justify-between px-4 py-2 bg-brand-bg/60">
                <span className="text-[10px] text-brand-muted uppercase tracking-wider font-medium">Check-in semanal</span>
                <label className="flex items-center gap-1.5 text-[11px] text-brand-muted cursor-pointer" data-testid="toggle-show-archived-label">
                  <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} className="w-3.5 h-3.5 accent-brand-green" data-testid="toggle-show-archived" />
                  Mostrar arquivadas
                </label>
              </div>
              <div className="grid grid-cols-[1fr_90px_90px_60px] gap-2 px-4 py-2.5 bg-brand-bg text-[10px] text-brand-muted font-medium uppercase tracking-wider">
                <span>Paciente</span><span className="text-center">Estado</span><span className="text-center">Último</span><span className="text-center">Ação</span>
              </div>
              {daily.patients.map(p => { const cfg = STATUS_CFG[p.status] || STATUS_CFG.em_falta; const Icon = cfg.icon; return (
                <div key={p.user_id} className="grid grid-cols-[1fr_90px_90px_60px] gap-2 items-center px-4 py-3 border-t border-brand-border/50 hover:bg-brand-bg/30" data-testid={`admin-dashboard-row-${p.user_id}`}>
                  <div className="min-w-0"><p className="text-sm font-medium text-brand-text truncate">{p.name}</p></div>
                  <div className="flex justify-center"><span className={`text-[10px] font-medium px-2 py-1 rounded-full flex items-center gap-1 ${cfg.color}`}><Icon size={11} weight="fill" /> {cfg.label}</span></div>
                  <span className="text-xs text-brand-muted text-center">{p.last_feedback || '-'}</span>
                  <div className="flex justify-center"><Link to={`/admin/pacientes/${p.user_id}`} className="text-brand-green hover:text-brand-green-hover p-1.5 inline-flex" data-testid={`admin-dashboard-patient-${p.user_id}`}><Eye size={16} /></Link></div>
                </div>
              ); })}
            </div>
          )}
          {/* Pending invoices */}
          {daily.pending_invoices?.length > 0 && (
            <div className="bg-white border border-brand-gold/20 rounded-2xl p-4 mb-5">

      {/* ═══ MANAGEMENT VIEW ═══ */}
              <h3 className="font-heading font-medium text-xs text-brand-gold-hover uppercase tracking-wider mb-3">Faturas por emitir ({daily.pending_invoices.length})</h3>
              <div className="space-y-2">
                {daily.pending_invoices.map(inv => (
                  <div key={inv.consultation_id} className="flex items-center justify-between p-2 bg-brand-bg/50 rounded-xl">
                    <div><p className="text-sm text-brand-text">{inv.patient_name}</p><p className="text-[10px] text-brand-muted">Consulta: {fmtDatePt(inv.date)}</p></div>
                    <Link to={`/admin/pacientes/${inv.patient_id}`} className="text-brand-green p-1.5 inline-flex" data-testid={`admin-dashboard-invoice-${inv.patient_id}`}><Eye size={16} /></Link>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
      {view === 'management' && mgmt && (
        <>
          <h2 className="font-heading font-medium text-2xl text-brand-text mb-5">Gestão clínica</h2>
          <div className="grid grid-cols-2 gap-3 mb-5">
            <button onClick={() => {}} className="bg-white border border-brand-border rounded-2xl p-4 text-left hover:border-brand-green/30 transition-all">
              <p className="text-3xl font-heading font-semibold text-brand-green">{mgmt.active_clients}</p>
              <p className="text-xs text-brand-muted mt-1">Clientes em acompanhamento ativo</p>
              <p className="text-[10px] text-brand-placeholder">{mgmt.total_clients} total</p>
            </button>
            <button onClick={() => openList('Sem consulta agendada', mgmt.no_apt_ids)} className="bg-white border border-brand-border rounded-2xl p-4 text-left hover:border-brand-green/30 transition-all">
              <p className={`text-3xl font-heading font-semibold ${mgmt.no_appointment > 0 ? 'text-red-500' : 'text-brand-green'}`}>{mgmt.no_appointment}</p>
              <p className="text-xs text-brand-muted mt-1">Sem consulta agendada</p>
            </button>
            <button onClick={() => openList('Sem interação há 30 dias', mgmt.no_interaction_ids)} className="bg-white border border-brand-border rounded-2xl p-4 text-left hover:border-brand-green/30 transition-all">
              <p className={`text-3xl font-heading font-semibold ${mgmt.no_interaction_30d > 0 ? 'text-brand-gold-hover' : 'text-brand-green'}`}>{mgmt.no_interaction_30d}</p>
              <p className="text-xs text-brand-muted mt-1">Sem interação há 30 dias</p>
            </button>
            <div className="bg-white border border-brand-border rounded-2xl p-4">
              <div className="flex gap-2 mb-2">
                <button onClick={() => setPeriod('7')} className={`text-[10px] px-2 py-0.5 rounded-full ${period === '7' ? 'bg-brand-green text-white' : 'text-brand-muted'}`}>7 dias</button>
                <button onClick={() => setPeriod('30')} className={`text-[10px] px-2 py-0.5 rounded-full ${period === '30' ? 'bg-brand-green text-white' : 'text-brand-muted'}`}>30 dias</button>
              </div>
              <p className="text-3xl font-heading font-semibold text-brand-text">{period === '7' ? mgmt.consultations_7d : mgmt.consultations_30d}</p>
              <p className="text-xs text-brand-muted mt-1">Consultas realizadas</p>
            </div>
          </div>
          {/* By tag */}
          <div className="bg-white border border-brand-border rounded-2xl p-4 mb-5">
            <h3 className="font-heading font-medium text-xs text-brand-muted uppercase tracking-wider mb-3">Clientes por tipo</h3>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(mgmt.by_tag).map(([tag, count]) => (
                <div key={tag} className="flex items-center justify-between p-2 bg-brand-bg/50 rounded-xl">
                  <span className="text-xs text-brand-text">{tag}</span>
                  <span className="text-sm font-heading font-semibold text-brand-text">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ═══ RECONTACT VIEW ═══ */}
      {view === 'recontact' && mgmt && (
        <>
          <h2 className="font-heading font-medium text-2xl text-brand-text mb-2">Clientes a recontactar</h2>
          <p className="text-sm text-brand-muted mb-5">Clientes que precisam de atenção</p>
          {mgmt.recontact?.length === 0 ? (
            <div className="text-center py-12"><CheckCircle size={40} className="mx-auto text-brand-green mb-3" weight="duotone" /><p className="text-brand-muted">Todos os clientes estão acompanhados.</p></div>
          ) : (
            <div className="space-y-2">
              {mgmt.recontact.map(r => (
                <div key={r.user_id} className={`bg-white border rounded-2xl p-4 ${r.contacted ? 'border-brand-border/50 opacity-60' : 'border-brand-border'}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-brand-text">{r.name}</p>
                      <p className="text-[10px] text-brand-muted">{r.email}</p>
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {r.reasons.map((reason, i) => <span key={i} className="text-[9px] bg-red-50 text-red-500 px-2 py-0.5 rounded-full">{reason}</span>)}
                      </div>
                    </div>
                    <div className="flex gap-1.5">
                      <Link to={`/admin/pacientes/${r.user_id}`} className="p-2 text-brand-green hover:bg-brand-green/5 rounded-lg inline-flex" data-testid={`admin-dashboard-reminder-${r.user_id}`}><Eye size={16} /></Link>
                      {!r.contacted && <button onClick={() => markContacted(r.user_id)} className="p-2 text-brand-muted hover:bg-brand-bg rounded-lg" title="Marcar como contactado"><Phone size={16} /></button>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ═══ LIST VIEW ═══ */}
      {view === 'list' && (
        <>
          <button onClick={() => setView('management')} className="text-sm text-brand-green mb-3 hover:underline">&larr; Voltar</button>
          <h2 className="font-heading font-medium text-xl text-brand-text mb-4">{listData.title} ({listData.ids.length})</h2>
          <div className="space-y-2">
            {listData.ids.map(id => { const p = getPatient(id); return (
              <Link key={id} to={`/admin/pacientes/${id}`} className="w-full text-left bg-white border border-brand-border rounded-2xl p-3 flex items-center justify-between hover:border-brand-green/30 no-underline text-inherit" data-testid={`admin-dashboard-silence-${id}`}>
                <div><p className="text-sm font-medium text-brand-text">{p.name}</p><p className="text-[10px] text-brand-muted">{p.email}</p></div>
                <Eye size={16} className="text-brand-green" />
              </Link>
            ); })}
          </div>
        </>
      )}
    </div>
  );
}
